<script lang="ts">
  import { get, writable } from "svelte/store";
  import ContactDropdown from "../../components/Business/ContactDropdown.svelte";
  import Switch from "../../components/Form/Fields/Switch.svelte";
  import ClfDropdown from "../../components/Form/Fields/Dropdown/ClfDropdown.svelte";
  import FormField from "../../components/Form/FormField.svelte";
  import Table from "../../components/Table/Table.svelte";
  import type { ICompanyContact, IContact } from "../../entities";
  import CustomFields from "../../components/CustomFields/CustomFields.svelte";
  import FormLayout from "../../layout/FormLayout.svelte";
  import type { IApiResponse } from "../../utils/api/api-response";
  import { companyContactDtoFields } from "../../utils/TableFields/company-contact-dto.fields";
  import ContactEditForm from "../Contact/ContactEditForm.svelte";
  import {
    companyContact,
    companyContacts,
    contactCustomFieldsSchemas,
    createNewContact,
    loadContacts,
    newContact,
    onDeleteContact,
    saveContact,
  } from "./company.store";
  import { initializeCustomFields } from "../../utils/CustomFieldEntities/custom-field-utils";

  const editing = writable<boolean>(false);
  let useExistingContact = 0;

  $: isNew = $companyContact != null && $companyContact.oid == 0;
  $: canSubmitExisting =
    $companyContact != null &&
    $companyContact.companyId != 0 &&
    $companyContact.contactId != 0;
  $: canSubmitNew = $newContact != null && $newContact.firstName != "";

  async function onSubmitExisting() {
    let _record = get(companyContact);
    let _resp: IApiResponse<ICompanyContact> = await saveContact(_record);
    onCancel();
    loadContacts(_resp.payload.companyId);
  }

  async function onSubmitNew() {
    let _record = get(newContact);
    _record.customFields = initializeCustomFields(
      get(contactCustomFieldsSchemas)
    );
    let _resp: IApiResponse<ICompanyContact> = await createNewContact(_record);
    onCancel();
    loadContacts(_resp.payload.companyId);
  }

  function onCancel() {
    editing.set(false);
    companyContact.set({} as ICompanyContact);
    newContact.set({} as IContact);
  }

  function onCreate() {
    let item = {
      oid: 0,
      mainCompany: 0,
    } as ICompanyContact;

    onEdit(item);
  }

  function onEdit(item: ICompanyContact) {
    companyContact.set(Object.assign({}, item));
    let _newContact = {} as IContact;
    _newContact.customFields = initializeCustomFields(
      get(contactCustomFieldsSchemas)
    );
    newContact.set(_newContact);
    editing.set(true);
  }
</script>

{#if $editing === false}
  <Table
    loading={false}
    fields={companyContactDtoFields}
    records={$companyContacts}
    pagination={null}
    createEnabled={true}
    editEnabled={true}
    deleteEnabled={true}
    on:create={(_) => onCreate()}
    on:edit={(e) => onEdit(e.detail.companyContact)}
    on:delete={(e) => onDeleteContact(e.detail)}
  />
{:else if $companyContact}
  <div class="columns">
    <div class="column is-12">
      {#if isNew}
        <div class="box has-text-centered add-contact-type">
          <div class="control">
            <label class="radio">
              <input type="radio" value={0} bind:group={useExistingContact} />
              Crea un nuovo contatto
            </label>
            <label class="radio">
              <input type="radio" value={1} bind:group={useExistingContact} />
              Associa un contatto esistente
            </label>
          </div>
        </div>
      {/if}

      {#if !isNew || useExistingContact == 1}
        <FormLayout
          canSubmit={canSubmitExisting}
          {isNew}
          createButtonText="Associa"
          on:submit={onSubmitExisting}
          on:cancel={onCancel}
        >
          <div class="columns">
            <div class="column is-6">
              <FormField>
                <ContactDropdown
                  required={true}
                  bind:value={$companyContact.contactId}
                />
              </FormField>
            </div>
            <div class="column is-6">
              <FormField>
                <Switch
                  name="Organizzazione Principale"
                  bind:value={$companyContact.mainCompany}
                />
              </FormField>
            </div>
          </div>
          <div  class="columns">  
            <div class="column is-6">
              <FormField>
                <ClfDropdown
                  name="Carica"
                  schema="JBPOSITION"
                  required={false}
                  clearVisible={true}
                  bind:value={$companyContact.clfJobPositionId}
                />
              </FormField>
            </div>

            <div class="column is-6">
              <FormField>
                <ClfDropdown
                  name="Ruolo"
                  schema="JBROLEXXXX"
                  required={false}
                  clearVisible={true}
                  bind:value={$companyContact.clfJobRoleId}
                />
              </FormField>
            </div>
          </div>
        </FormLayout>
      {/if}

      {#if isNew && useExistingContact == 0}
        <FormLayout
          canSubmit={canSubmitNew}
          isNew={true}
          on:submit={onSubmitNew}
          on:cancel={onCancel}
        >
          <ContactEditForm record={newContact} />
          <CustomFields
            schemas={$contactCustomFieldsSchemas}
            values={$newContact.customFields}
          />
        </FormLayout>
      {/if}
    </div>
  </div>
{/if}

<style>
  .add-contact-type {
    margin: auto;
    margin-bottom: 2rem;
  }
  .add-contact-type .control .radio {
    margin-left: 2rem;
    margin-right: 2rem;
  }
</style>
